<template>
  <v-container fluid>
    <v-row>
      <v-col class="text-h3 gray--text">
        História da Patologia Pregressa
      </v-col>

      <v-col cols="auto" class="mt-2">
        <SaveStatus
          :saving="saveManager.isSaving"
          :pending="saveManager.pendingSave"
          :saved-at="historyPathology.updatedAt"
          :error="hasErrors || saveManager.isError"
          :loading="isLoading"
          not-saved-message="O H.P.P. ainda não foi salvo."
        />
      </v-col>

      <v-col cols="auto">
        <v-btn
          color="light-blue"
          dark
          @click="saveManager.triggerManualSave()"
          :loading="isLoading || saveManager.isSaving"
          :disabled="attendanceEsigned"
        >
          Salvar H.P.P.
        </v-btn>
      </v-col>
    </v-row>

    <MedicalRecordHppSkeletonLoader v-if="isLoading" />
    <v-row v-else>
      <v-col>
        <v-row dense>
          <v-col>
            <v-combobox
              v-model="facilitator"
              outlined
              dense
              hide-details
              label="Introdução"
              :items="facilitator_introductions"
              @change="handleFacilitatorChange"
              background-color="white"
              :disabled="attendanceEsigned"
            />
          </v-col>
        </v-row>

        <v-row dense>
          <v-col>
            <v-combobox
              v-model="facilitator"
              outlined
              dense
              hide-details
              label="Patologia"
              :items="facilitator_pathology"
              @change="handleFacilitatorChange"
              background-color="white"
              :disabled="attendanceEsigned"
            />
          </v-col>
        </v-row>

        <v-row dense>
          <v-col>
            <v-combobox
              v-model="facilitator"
              outlined
              dense
              hide-details
              label="Procedimento"
              :items="facilitator_procedure"
              @change="handleFacilitatorChange"
              background-color="white"
              :disabled="attendanceEsigned"
            />
          </v-col>
        </v-row>

        <v-row dense>
          <v-col>
            <v-combobox
              v-model="facilitator"
              outlined
              dense
              hide-details
              label="Local"
              :items="facilitator_site"
              @change="handleFacilitatorChange"
              background-color="white"
              :disabled="attendanceEsigned"
            />
          </v-col>
        </v-row>

        <v-row dense>
          <v-col>
            <v-combobox
              v-model="facilitator"
              outlined
              dense
              hide-details
              label="Tempo"
              :items="facilitator_period"
              @change="handleFacilitatorChange"
              background-color="white"
              :disabled="attendanceEsigned"
            />
          </v-col>
        </v-row>
      </v-col>

      <v-col>
        <v-row dense>
          <v-col>
            <v-textarea
              v-model="historyPathology.description"
              v-uppercase
              solo
              dense
              rows="13"
              placeholder="Digite a história patológica pregressa..."
              background-color="white"
              :hide-details="!hasErrors"
              :error-messages="errors.description"
              :disabled="attendanceEsigned"
              @input="handleFormChange"
            />
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <Allergy
      :attendance="attendance"
      :showForm="!attendanceEsigned"
      :allowDelete="!attendanceEsigned"
    />
    <MedicationHistory
      :attendance="attendance"
      :showForm="!attendanceEsigned"
      :allowDelete="!attendanceEsigned"
    />

    <v-row v-if="historyPathology.id">
      <v-col>
        <FileUpload
          ownerType="HistoryPathology"
          :ownerId="historyPathology.id"
          :disabled="attendanceEsigned"
        />
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import GET_HISTORY_PATHOLOGY_FACILITATORS from "@/graphql/history_pathology_facilitators.gql";
import GET_HISTORY_PATHOLOGY from "@/graphql/history_pathology.gql";
import SAVE_HISTORY_PATHOLOGY from "@/graphql/save_history_pathology.gql";

import MedicalRecordHppSkeletonLoader from "@/components/medical_record/hpp/skeleton_loader.vue";
import SaveStatus from "@/components/ui/form/save_status/index.vue";
import Allergy from "@/components/medical_record/hpp/allergy.vue";
import MedicationHistory from "@/components/medical_record/hpp/medication_history.vue";

import { AutoSaveManager } from "@/services/AutoSaveManager/index.js";

export default {
  name: "HPP",
  components: {
    MedicalRecordHppSkeletonLoader,
    SaveStatus,
    Allergy,
    MedicationHistory,
  },
  props: {
    attendance: Object,
    attendanceEsigned: Boolean,
  },
  data() {
    return {
      historyPathologyFacilitators: [],
      historyPathology: { description: "" },
      facilitator: [],
      introductionFacilitator: [],
      errors: {},
      saveManager: null,
    };
  },
  apollo: {
    historyPathologyFacilitators: {
      query: GET_HISTORY_PATHOLOGY_FACILITATORS,
    },
    historyPathology: {
      query: GET_HISTORY_PATHOLOGY,
      fetchPolicy: "no-cache",
      variables() {
        return {
          attendanceId: this.attendance.id,
        };
      },
    },
  },
  created() {
    this.saveManager = new AutoSaveManager({
      formName: "H.P.P",
      beforeSave: () => this.beforeSave(),
      onSave: () => this.onSave(),
    });
  },
  watch: {
    $route() {
      this.saveManager.triggerManualSave();
    },
  },
  computed: {
    isLoading() {
      if (this.$apollo.queries.historyPathologyFacilitators.loading) {
        return true;
      }

      return !this.historyPathology.id;
    },
    facilitator_introductions() {
      return this.lodash.filter(this.orderedfacilitators, {
        facilitatorType: "introduction",
      });
    },
    facilitator_pathology() {
      return this.lodash.filter(this.orderedfacilitators, {
        facilitatorType: "pathology",
      });
    },
    facilitator_procedure() {
      return this.lodash.filter(this.orderedfacilitators, {
        facilitatorType: "procedure",
      });
    },
    facilitator_site() {
      return this.lodash.filter(this.orderedfacilitators, {
        facilitatorType: "site",
      });
    },
    facilitator_period() {
      return this.lodash.filter(this.orderedfacilitators, {
        facilitatorType: "period",
      });
    },
    orderedfacilitators() {
      return this.lodash.orderBy(
        this.historyPathologyFacilitators,
        [
          (e) =>
            e.text
              .toLowerCase()
              .normalize("NFD")
              .replace(/[\u0300-\u036f]/g, ""),
        ],
        ["asc"]
      );
    },
    hasErrors() {
      return Object.keys(this.errors).length > 0;
    },
  },
  methods: {
    handleFacilitatorChange(e, breakLine = false) {
      if (this.attendanceEsigned) return;

      if (e) {
        if (this.historyPathology.description !== null) {
          if (breakLine) {
            this.historyPathology.description += "\r\n";
            this.historyPathology.description += `${e.text}`;
          } else {
            this.historyPathology.description += ` ${e.text}`;
          }

          this.historyPathology.description = this.historyPathology.description.trim();
        } else {
          this.historyPathology.description = ` ${e.text}`;
        }
      }

      this.facilitator = [];
      this.handleFormChange();
    },
    handleFormChange() {
      this.pendingSave = true;
      this.saveManager.dispatchAutoSave();
    },
    beforeSave() {
      this.validateForm();

      if (this.hasErrors) return false;
      if (this.attendanceEsigned) return false;

      return true;
    },
    async onSave() {
      const description = this.historyPathology.description || "";

      const { data } = await this.apolloMutate({
        mutation: SAVE_HISTORY_PATHOLOGY,
        variables: {
          attendanceId: this.attendance.id,
          description,
        },
      });

      this.historyPathology.updatedAt = data.saveHistoryPathology.updatedAt;
    },
    validateForm() {
      this.errors = {};

      if (!this.historyPathology.description?.trim()) {
        this.errors.description = "O H.P.P não pode estar vazio.";
      }
    },
  },
};
</script>

<style lang="scss" scoped></style>
